import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Button from "../../../components/ui/Button"
import ReauthModal from "../../../components/ui/ReauthModal"
import { Card, CardContent } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { listLearningPaths, createLearningPath, updateLearningPath, deleteLearningPath } from "../../../utils/learningPathHelpers"
import { syncPathToCatalog, removePathFromCatalog } from "../../../utils/catalogHelpers"
import { listPrograms } from "../../../utils/programHelpers"
import { listCourses } from "../../../utils/courseHelpers"
import LearningPathBuilder from "../../organization-program-builder/components/LearningPathBuilder"

const PathFormModal = ({ initialPath, onClose, onSave }) => {
  const [title, setTitle] = useState(initialPath?.title || "")
  const [description, setDescription] = useState(initialPath?.description || "")
  const [error, setError] = useState("")
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e) => {
    e?.preventDefault()
    if (!title?.trim()) {
      setError("Title is required")
      return
    }
    setSaving(true)
    await onSave({ title, description })
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <Card className="max-w-md w-full dark:bg-card">
        <div className="flex items-center justify-between px-5">
          <h3 className="text-lg font-semibold text-foreground">{initialPath ? "Edit" : "New"} Learning Path</h3>
          <Button variant="ghost" size="icon" onClick={onClose} iconName="X" iconSize={18} />
        </div>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input label="Title" placeholder="e.g. Web3 Foundations Track" value={title} onChange={(e) => setTitle(e?.target?.value)} required />
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Description (optional)</label>
              <textarea
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e?.target?.value)}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" fullWidth onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="default" fullWidth loading={saving}>Save</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

const LearningPathsPanel = ({ organization }) => {
  const navigate = useNavigate()
  const orgId = organization?.orgId
  const [standalonePaths, setStandalonePaths] = useState([])
  const [programPaths, setProgramPaths] = useState([])
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingPath, setEditingPath] = useState(null)
  const [openBuilderId, setOpenBuilderId] = useState(null)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)

  const loadData = async () => {
    setLoading(true)
    const [{ paths }, { programs }, { courses: courseList }] = await Promise.all([
      listLearningPaths(orgId),
      listPrograms(orgId),
      listCourses(orgId),
    ])
    setStandalonePaths(paths || [])
    setProgramPaths(programs?.filter((p) => p?.learningPath?.length > 0) || [])
    setCourses(courseList || [])
    setLoading(false)
  }

  useEffect(() => {
    if (orgId) loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const handleCreateOrEdit = async ({ title, description }) => {
    if (editingPath) {
      await updateLearningPath(orgId, editingPath.pathId, { title, description })
    } else {
      await createLearningPath({ orgId, title, description })
    }
    setShowFormModal(false)
    setEditingPath(null)
    loadData()
  }

  const handleNodesChange = async (pathId, nextNodes) => {
    setStandalonePaths((prev) => prev.map((p) => (p.pathId === pathId ? { ...p, nodes: nextNodes } : p)))
    await updateLearningPath(orgId, pathId, { nodes: nextNodes })
  }

  const handleTogglePublish = async (path) => {
    const nextPublished = !path?.isPublished
    setStandalonePaths((prev) => prev.map((p) => (p.pathId === path.pathId ? { ...p, isPublished: nextPublished } : p)))
    await updateLearningPath(orgId, path.pathId, { isPublished: nextPublished })

    if (nextPublished) {
      await syncPathToCatalog({
        orgId,
        orgName: organization?.name,
        orgVerified: organization?.verified,
        orgNesolearnId: organization?.nesolearnId,
        path,
      })
    } else {
      await removePathFromCatalog(path.pathId)
    }
  }

  const handleDelete = (pathId) => {
    if (!window.confirm("Delete this learning path?")) return
    setPendingDeleteId(pathId)
  }

  const confirmDelete = async () => {
    await deleteLearningPath(orgId, pendingDeleteId)
    setPendingDeleteId(null)
    loadData()
  }

  const courseTitle = (courseId) => courses?.find((c) => c?.courseId === courseId)?.title || "Untitled Course"

  return (
    <div className="space-y-8 md:col-span-4">
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Standalone Paths</h2>
            <p className="text-xs text-muted-foreground">Reusable sequences not tied to a single program.</p>
          </div>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => { setEditingPath(null); setShowFormModal(true) }}>
            New Path
          </Button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-muted-foreground text-sm">Loading...</div>
        ) : standalonePaths?.length === 0 ? (
          <Card className="p-12 text-center dark:bg-transparent">
            <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Route" size={24} className="text-accent" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-1">No standalone paths yet</h3>
            <p className="text-muted-foreground text-sm mb-4">Sequence any of your courses into a reusable path.</p>
            <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowFormModal(true)}>New Path</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {standalonePaths?.map((path) => (
              <Card key={path.pathId} className="dark:bg-transparent">
                <CardContent className="pt-5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className="font-semibold text-foreground text-sm">{path.title}</h4>
                      {path.description && <p className="text-xs text-muted-foreground mt-0.5">{path.description}</p>}
                      <p className="text-xs text-muted-foreground mt-1">{path.nodes?.length || 0} courses</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge variant={path?.isPublished ? "default" : "outline"} className="mr-1">
                        {path?.isPublished ? "Published" : "Draft"}
                      </Badge>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleTogglePublish(path)}
                        disabled={!path?.nodes?.length}
                        title={!path?.nodes?.length ? "Add at least one course before publishing" : undefined}
                      >
                        {path?.isPublished ? "Unpublish" : "Publish"}
                      </Button>
                      <Button variant="ghost" size="icon" iconName={openBuilderId === path.pathId ? "ChevronUp" : "ChevronDown"} iconSize={16} onClick={() => setOpenBuilderId(openBuilderId === path.pathId ? null : path.pathId)} />
                      <Button variant="ghost" size="icon" iconName="Pencil" iconSize={16} onClick={() => { setEditingPath(path); setShowFormModal(true) }} />
                      <Button variant="ghost" size="icon" iconName="Trash2" iconSize={16} onClick={() => handleDelete(path.pathId)} />
                    </div>
                  </div>

                  {openBuilderId === path.pathId && (
                    <div className="pt-2 border-t border-border">
                      <LearningPathBuilder
                        path={path.nodes || []}
                        bundledCourseIds={courses?.map((c) => c.courseId) || []}
                        courses={courses}
                        onChange={(nextNodes) => handleNodesChange(path.pathId, nextNodes)}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-1">Program Paths</h2>
        <p className="text-xs text-muted-foreground mb-4">Sequences built inside a program — edit them from that program.</p>

        {programPaths?.length === 0 ? (
          <p className="text-sm text-muted-foreground">No programs have a learning path sequenced yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {programPaths?.map((program) => (
              <Card key={program.programId} className="p-0 dark:bg-transparent">
                <CardContent className="pt-5 pb-5 space-y-2">
                  <h4 className="font-semibold text-foreground text-sm">{program.title}</h4>
                  <p className="text-xs text-muted-foreground">{program.learningPath?.length || 0} steps</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {program.learningPath?.map((n) => courseTitle(n?.courseId))?.join(" → ")}
                  </p>
                  <Button
                    variant="outline" size="sm" fullWidth iconName="Route" iconPosition="left"
                    onClick={() => navigate(`/organization-program-builder?orgId=${orgId}&programId=${program.programId}`)}
                  >
                    Edit in Program
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {showFormModal && (
        <PathFormModal initialPath={editingPath} onClose={() => { setShowFormModal(false); setEditingPath(null) }} onSave={handleCreateOrEdit} />
      )}

      <ReauthModal open={!!pendingDeleteId} label="delete this learning path" onConfirm={confirmDelete} onClose={() => setPendingDeleteId(null)} />
    </div>
  )
}

export default LearningPathsPanel